import type { ThemeColors, ThemeColorsMap, ThemeShades } from '/#/common/theme'
import type { Ref } from 'vue'

import get from 'lodash-es/get'
import keyBy from 'lodash-es/keyBy'
import map from 'lodash-es/map'
import mapValues from 'lodash-es/mapValues'
import pick from 'lodash-es/pick'
import resolveConfig from 'tailwindcss/resolveConfig'

import tailwindConfig from '../../tailwind.config'

/**
 * @name useThemeColors
 * @description A composable that provides access to theme colors and color scales from Tailwind config.
 * This composable helps manage and access theme colors consistently across the application.
 *
 * @param {ThemeShades} scale - The color scale to use (50-900, default 500)
 *
 * @returns {Object} An object containing:
 * - defaultColors: Ref<ThemeColorsMap> - Reactive map of all theme colors at the specified scale
 * - getColorScales: (colorName: ThemeColors, startScale?: ThemeShades, endScale?: ThemeShades) => string[]
 *   Function to get an array of color values for a specific color across different scales
 * - statusColors: Ref<ThemeColorsMap> - Reactive map of status-specific colors (success, danger, warning, info)
 *
 * @example
 * const { defaultColors, getColorScales, statusColors } = useThemeColors(500)
 *
 * // Get all default colors at scale 500
 * console.log(defaultColors.value)
 *
 * // Get color scales for 'primary' from 100 to 700
 * const primaryScales = getColorScales('primary', 100, 700)
 *
 * // Access status colors
 * console.log(statusColors.value.success)
 */

export const useThemeColors = (scale: ThemeShades = 500) => {
  const { theme } = resolveConfig(tailwindConfig)

  /**
   * @name defaultColors
   * @description Default colors from tailwind config
   */
  const defaultColors: Ref<ThemeColorsMap> = ref(
    mapValues(
      keyBy(
        map(theme.colors, (value, key) => ({
          key,
          value: get(value, scale),
        })),
        'key',
      ),
      'value',
    ),
  )

  /**
   * @name getColorScales
   * @description Retrieves color scales based on the given color name from a predefined color theme.
   * If the specified scales use a different scale, you can specify the start and end scales.
   * @param colorName The name of the color.
   * @param startScale The start scale of the color.
   * @param endScale The end scale of the color.
   */
  const getColorScales = (
    colorName: ThemeColors,
    startScale: ThemeShades = 50,
    endScale: ThemeShades = 900,
  ): any[] => {
    const color = theme.colors[colorName as keyof typeof theme.colors]
    if (!color || typeof color !== 'object' || Array.isArray(color)) return []

    const scales = Object.keys(color)
      .map(Number)
      .filter((scale) => scale >= startScale && scale <= endScale)
      .sort((a, b) => a - b)

    return scales.map((scale) => color[scale as ThemeShades])
  }

  const statusColors: Ref<ThemeColorsMap> = ref(
    pick(theme.colors, [
      'success',
      'danger',
      'warning',
      'info',
      'primary',
      'secondary',
    ]) as ThemeColorsMap,
  )

  return {
    defaultColors,
    statusColors,
    getColorScales,
    // Type conversion is necessary because our configurations do not match with the Tailwind config.
    // However, when type conversion is applied, the specified type does not work correctly.
    // @ts-ignore
    colors: theme.colors as Record<ThemeColors, Record<ThemeShades, string>>,
  }
}
